import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import {
  AlertCircle,
  ArrowDownLeft,
  ArrowUpRight,
  CalendarDays,
  FileText,
  Landmark,
  Loader2,
  ReceiptText,
  Unlink2,
  Wallet,
  X
} from 'lucide-react';
import { formatCurrency } from '../../utils/formatters';

const currencySymbols = {
  TRY: '₺',
  USD: '$',
  EUR: '€',
  GBP: '£'
};

const typeLabels = {
  Inflow: 'Giriş',
  Outflow: 'Çıkış'
};

const accountTypeLabels = {
  Cash: 'Kasa',
  Bank: 'Banka',
  CreditCard: 'Kredi Kartı'
};

const formatDate = (date) => {
  if (!date) return '-';
  return new Date(date).toLocaleDateString('tr-TR', {
    day: '2-digit',
    month: 'long',
    year: 'numeric'
  });
};

const DetailItem = ({ icon, label, value, hint }) => (
  <div className="min-w-0 rounded-2xl border border-slate-100 bg-white p-3 shadow-sm dark:border-slate-800 dark:bg-slate-900/70">
    <div className="mb-2 flex items-center gap-2 text-[10px] font-black uppercase tracking-widest text-slate-400">
      {React.createElement(icon, { size: 14 })}
      {label}
    </div>
    <div className="break-words text-sm font-black text-slate-800 dark:text-slate-100">{value || '-'}</div>
    {hint && <div className="mt-1 break-words text-xs font-bold text-slate-400">{hint}</div>}
  </div>
);

const TransactionDetailModal = ({ isOpen, onClose, transactionId, onUpdated, canUnlink = true }) => {
  const [transaction, setTransaction] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [unlinkingId, setUnlinkingId] = useState('');

  const fetchTransaction = useCallback(async (signal) => {
    if (!transactionId) return;

    setLoading(true);
    setError('');

    try {
      const response = await axios.get(`/api/transactions/${transactionId}`, { signal });
      setTransaction(response.data?.data || null);
    } catch (err) {
      if (err.name !== 'CanceledError') {
        setError(err.response?.data?.message || 'İşlem detayı getirilemedi.');
        setTransaction(null);
      }
    } finally {
      if (!signal?.aborted) {
        setLoading(false);
      }
    }
  }, [transactionId]);

  useEffect(() => {
    if (!isOpen || !transactionId) {
      setTransaction(null);
      setError('');
      return;
    }

    const controller = new AbortController();
    fetchTransaction(controller.signal);

    return () => controller.abort();
  }, [fetchTransaction, isOpen, transactionId]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') onClose();
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  const allocations = transaction?.allocations || [];
  const firstInvoice = allocations[0]?.invoiceId;
  const currency = transaction?.financialAccount?.currency || firstInvoice?.currency || 'TRY';
  const symbol = currencySymbols[currency] || currency;
  const rawSavedExchangeRate = Number(
    transaction?.exchangeRate ||
    allocations[0]?.exchangeRate ||
    firstInvoice?.exchangeRate ||
    0
  );
  const savedExchangeRate = rawSavedExchangeRate > 0 ? rawSavedExchangeRate : 1;
  const hasSavedExchangeRate = rawSavedExchangeRate > 0;
  const hasForeignCurrency = currency !== 'TRY';
  const tryEquivalent = Number(transaction?.amount || 0) * savedExchangeRate;
  const isOutflow = transaction?.type === 'Outflow';
  const operationName = transaction?.operation?.name || transaction?.operation?.code || transaction?.operation?.projectNo;
  const personnelAccount = transaction?.personelAccountId;

  const badgeClass = useMemo(() => (
    isOutflow
      ? 'bg-rose-50 text-rose-700 ring-rose-100 dark:bg-rose-500/10 dark:text-rose-300 dark:ring-rose-500/20'
      : 'bg-emerald-50 text-emerald-700 ring-emerald-100 dark:bg-emerald-500/10 dark:text-emerald-300 dark:ring-emerald-500/20'
  ), [isOutflow]);

  const handleUnlinkAllocation = async (allocation) => {
    if (!canUnlink || unlinkingId) return;
    const invoiceId = allocation?.invoiceId?._id || allocation?.invoiceId;
    const allocationKey = allocation?._id || invoiceId;
    if (!transaction?._id || !invoiceId) {
      toast.error('Kaldırılacak fatura ilişkisi bulunamadı.');
      return;
    }

    setUnlinkingId(String(allocationKey));
    try {
      await axios.post(`/api/transactions/${transaction._id}/unallocate`, { invoiceId });
      toast.success('Fatura ilişkisi kaldırıldı.');
      await fetchTransaction();
      onUpdated?.();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Fatura ilişkisi kaldırılamadı.');
    } finally {
      setUnlinkingId('');
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[60] flex max-h-screen items-center justify-center bg-slate-950/60 p-4 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="flex max-h-[calc(100dvh-2rem)] w-full max-w-6xl flex-col overflow-hidden rounded-[2rem] border border-white/20 bg-slate-50 shadow-2xl animate-in fade-in zoom-in-95 duration-200 dark:border-slate-800 dark:bg-slate-950"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex shrink-0 items-start justify-between gap-4 border-b border-slate-200/70 bg-white px-6 py-5 dark:border-slate-800 dark:bg-slate-900">
          <div className="min-w-0">
            <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">Finansal Hareket Detayı</p>
            <h2 className="mt-1 max-h-20 overflow-y-auto break-words text-xl font-black tracking-tight text-slate-900 dark:text-white">
              {transaction?.description || 'İşlem detayı'}
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="shrink-0 rounded-2xl p-2 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700 dark:hover:bg-slate-800 dark:hover:text-slate-100"
            aria-label="Kapat"
          >
            <X size={22} />
          </button>
        </div>

        <div className="min-h-0 max-h-[85vh] overflow-y-auto overscroll-contain p-4 pr-2 sm:p-6 sm:pr-2">
          {loading ? (
            <div className="flex min-h-80 flex-col items-center justify-center gap-3 text-slate-400">
              <Loader2 size={34} className="animate-spin text-blue-600" />
              <p className="text-xs font-black uppercase tracking-widest">Detaylar yükleniyor</p>
            </div>
          ) : error ? (
            <div className="flex min-h-80 flex-col items-center justify-center gap-3 rounded-3xl border border-rose-100 bg-rose-50 text-rose-600 dark:border-rose-500/20 dark:bg-rose-500/10">
              <AlertCircle size={34} />
              <p className="text-sm font-black">{error}</p>
            </div>
          ) : (
            <div className="grid items-start gap-4 lg:grid-cols-[minmax(0,1.2fr)_minmax(0,1fr)]">
              <div className="min-w-0 space-y-4">
                <section className="min-w-0">
                  <div className="mb-3 flex items-center gap-2">
                    <ReceiptText size={18} className="text-blue-600" />
                    <h3 className="text-sm font-black uppercase tracking-widest text-slate-700 dark:text-slate-200">Genel Bilgiler</h3>
                  </div>
                  <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                    <div className="min-w-0 rounded-2xl border border-slate-100 bg-white p-3 shadow-sm dark:border-slate-800 dark:bg-slate-900/70">
                      <div className="mb-2 text-[10px] font-black uppercase tracking-widest text-slate-400">İşlem Tipi</div>
                      <span className={`inline-flex items-center gap-2 rounded-xl px-3 py-1.5 text-xs font-black ring-1 ${badgeClass}`}>
                        {isOutflow ? <ArrowDownLeft size={14} /> : <ArrowUpRight size={14} />}
                        {typeLabels[transaction?.type] || transaction?.type || '-'}
                      </span>
                    </div>
                    <DetailItem icon={CalendarDays} label="Tarih" value={formatDate(transaction?.date)} />
                    <DetailItem icon={Wallet} label="Para Birimi" value={currency} hint={`Sembol: ${symbol}`} />
                  </div>
                </section>

                <section className="min-w-0">
                  <div className="mb-3 flex items-center gap-2">
                    <Landmark size={18} className="text-indigo-600" />
                    <h3 className="text-sm font-black uppercase tracking-widest text-slate-700 dark:text-slate-200">Hesap ve Operasyon Bilgileri</h3>
                  </div>
                  <dl className="grid grid-cols-1 gap-x-4 gap-y-3 rounded-xl border border-slate-100 bg-white p-4 dark:border-slate-800 dark:bg-slate-900/70 md:grid-cols-2">
                    {[
                      {
                        label: 'Kasa / Banka',
                        value: transaction?.financialAccount?.name,
                        hint: accountTypeLabels[transaction?.financialAccount?.type] || transaction?.financialAccount?.type
                      },
                      { label: 'Hesap Para Birimi', value: transaction?.financialAccount?.currency || currency },
                      { label: 'Operasyon', value: operationName, hint: transaction?.operation?.status },
                      { label: 'Personel', value: personnelAccount?.name, hint: personnelAccount?.code || personnelAccount?.phone }
                    ].map(({ label, value, hint }) => (
                      <div key={label} className="min-w-0">
                        <dt className="text-xs font-bold text-slate-500 dark:text-slate-400">{label}</dt>
                        <dd className="mt-1 break-words text-sm font-semibold text-slate-800 dark:text-slate-100">
                          {value || '-'}
                          {hint && <span className="font-normal text-slate-500 dark:text-slate-400"> ({hint})</span>}
                        </dd>
                      </div>
                    ))}
                  </dl>
                </section>

                <section className="w-full min-w-0">
                  <div className="mb-3 text-[10px] font-black uppercase tracking-widest text-slate-400">Açıklama</div>
                  <div className="w-full whitespace-pre-wrap break-words rounded-xl border border-slate-100 bg-white p-4 text-sm font-semibold leading-6 text-slate-600 dark:border-slate-800 dark:bg-slate-900/70 dark:text-slate-300">
                    {transaction?.description || '-'}
                  </div>
                </section>
              </div>

              <div className="min-w-0 space-y-4">
                <aside className="min-w-0 rounded-2xl bg-slate-900 p-4 text-white shadow-xl dark:bg-white dark:text-slate-950">
                  <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-2xl bg-white/10 dark:bg-slate-950/10">
                    {isOutflow ? <ArrowDownLeft size={24} /> : <ArrowUpRight size={24} />}
                  </div>
                  <p className="text-[10px] font-black uppercase tracking-widest text-white/50 dark:text-slate-500">Net Tutar</p>
                  <div className={`mt-2 break-words text-2xl font-black ${isOutflow ? 'text-rose-300 dark:text-rose-600' : 'text-emerald-300 dark:text-emerald-600'}`}>
                    {isOutflow ? '-' : '+'}{formatCurrency(transaction?.amount || 0, currency)}
                  </div>
                  <div className="mt-3 rounded-xl bg-white/10 p-3 dark:bg-slate-950/10">
                    <div className="text-[10px] font-black uppercase tracking-widest text-white/50 dark:text-slate-500">Kategori</div>
                    <div className="mt-1 text-sm font-black">{transaction?.category || '-'}</div>
                  </div>

                  {hasForeignCurrency && (
                    <div className="mt-4 rounded-2xl border border-white/10 bg-white/5 p-4 backdrop-blur-sm dark:border-slate-200/10 dark:bg-slate-950/5">
                      <div className="text-[10px] font-black uppercase tracking-widest text-white/50 dark:text-slate-500">
                        Kur Bilgisi
                      </div>
                      <div className="mt-2 grid gap-2 sm:grid-cols-2">
                        <div className="rounded-2xl bg-white/10 px-4 py-3 dark:bg-slate-950/10">
                          <div className="text-[10px] font-black uppercase tracking-widest text-white/50 dark:text-slate-500">
                            İşlem Kuru
                          </div>
                          <div className="mt-1 text-sm font-black">
                            {hasSavedExchangeRate
                              ? `1 ${currency} = ${formatCurrency(savedExchangeRate, 'TRY')} TRY`
                              : '1.00 (TRY)'}
                          </div>
                        </div>
                        <div className="rounded-2xl bg-white/10 px-4 py-3 dark:bg-slate-950/10">
                          <div className="text-[10px] font-black uppercase tracking-widest text-white/50 dark:text-slate-500">
                            TL Karşılığı
                          </div>
                          <div className="mt-1 text-sm font-black">
                            {formatCurrency(tryEquivalent, 'TRY')}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </aside>
                <section>
                  <div className="mb-3 flex items-center gap-2">
                    <FileText size={18} className="text-rose-600" />
                    <h3 className="text-sm font-black uppercase tracking-widest text-slate-700 dark:text-slate-200">Fatura İlişkileri ({allocations.length})</h3>
                  </div>
                  {allocations.length > 0 ? (
                    <div className="space-y-2">
                      {allocations.map((allocation) => {
                        const invoice = allocation.invoiceId || {};
                        const sourceAmount = allocation.sourceAmount ?? allocation.amount ?? 0;
                        const invoiceAmount = allocation.invoiceAmount ?? allocation.allocatedAmount ?? 0;
                        const fxDifferenceAmount = allocation.fxDifferenceAmount ?? 0;
                        const sourceAllocationCurrency = allocation.sourceCurrency || currency;
                        const invoiceAllocationCurrency = allocation.invoiceCurrency || invoice.currency || currency;
                        return (
                          <div
                            key={allocation._id || invoice._id}
                            className="grid min-w-0 grid-cols-[minmax(0,1fr)_auto] items-center gap-x-3 gap-y-1 rounded-xl border border-slate-100 bg-white px-3 py-2 text-sm font-black text-slate-800 shadow-sm dark:border-slate-800 dark:bg-slate-900/70 dark:text-slate-100"
                          >
                            <div className="col-start-1 row-start-1 flex min-w-0 items-center gap-2 break-all">
                              <FileText size={15} className="text-rose-500" />
                              {invoice.invoiceNumber || 'Numarasız'}
                            </div>
                            <div className="col-start-1 break-words text-xs font-medium text-slate-500 dark:text-slate-400">
                              Kaynak: {formatCurrency(sourceAmount, sourceAllocationCurrency)} | Fatura: {formatCurrency(invoiceAmount, invoiceAllocationCurrency)}
                            </div>
                            {allocation.fxDifferenceType && allocation.fxDifferenceType !== 'None' && (
                              <div className="col-start-1 break-words text-xs font-medium text-amber-600">
                                Kur farkı: {formatCurrency(fxDifferenceAmount, invoiceAllocationCurrency)} ({allocation.fxDifferenceType})
                              </div>
                            )}
                            {canUnlink && (
                              <button
                                type="button"
                                onClick={() => handleUnlinkAllocation(allocation)}
                                disabled={Boolean(unlinkingId)}
                                aria-label={`${invoice.invoiceNumber || 'Fatura'} ilişkisini kaldır`}
                                className="col-start-2 row-start-1 inline-flex items-center justify-center gap-1 self-start rounded-lg bg-rose-50 px-2 py-1 text-[10px] font-black uppercase tracking-widest text-rose-600 transition-colors hover:bg-rose-100 disabled:opacity-50"
                              >
                                {unlinkingId === String(allocation._id || invoice._id)
                                  ? <Loader2 size={12} className="animate-spin" />
                                  : <Unlink2 size={12} />}
                                Kaldır
                              </button>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="rounded-3xl border border-dashed border-slate-200 bg-white p-6 text-sm font-bold text-slate-400 dark:border-slate-800 dark:bg-slate-900/70">
                      Bu işlem henüz bir faturaya ilişkilendirilmedi.
                    </div>
                  )}
                </section>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TransactionDetailModal;
